'use client';

import { useEffect, useRef, useState } from 'react';
import { LogoWatermarkLayer } from '@/components/public/v2/LogoWatermark';
import { DrawnEffectLayer } from '@/components/public/v2/effects/DrawnEffects';
import {
  normaliseBackgroundEffect,
  normaliseWatermarkPlacement,
  type BackgroundEffect,
  type LegacyBackgroundEffect,
} from '@/lib/public/effects/catalogue';
import { effectSeed } from '@/lib/public/effects/seed';
import { outlineWordFor } from '@/lib/public/effects/shapes';

/**
 * What the readers pass: today's effects, or a value saved before the
 * October 2026 refresh, which draws as its replacement (see
 * normaliseBackgroundEffect). Kept wide so a stored string can be passed
 * straight through.
 */
export type BackgroundEffectType = BackgroundEffect | LegacyBackgroundEffect;

interface V2BackgroundEffectProps {
  effect: BackgroundEffectType | string;
  /** Logo watermark only: the logo to draw, the page colour and the ink to draw it in. */
  logoUrl?: string | null;
  page?: string;
  tint?: string;
  /** The organiser's accent. Absent, it is read from --theme-secondary on the page wrapper. */
  accent?: string;
  /** Seeds Topo, Sprint and Chalk, and gives Outline type its word. Absent, read from the page's h1. */
  eventName?: string | null;
  /** Outline type falls back to the event's year, in its own timezone. */
  startDate?: string | Date | null;
  timezone?: string | null;
  /** Logo watermark: 'left' | 'centre' | 'right' (landingThemeWatermarkPlacement); absent is right. */
  watermarkPlacement?: string | null;
  /** Outline type: the organiser's own word; absent or empty draws the automatic one. */
  outlineWord?: string | null;
}

/**
 * Every moving effect carries .v2-fx-motion, so one rule stops them all for
 * visitors who ask their device for less motion (they used to keep moving).
 * The drawn effects sit over the header and fade out; their height is set
 * here so a phone's address bar showing and hiding never resizes them.
 */
const REDUCED_MOTION_CSS = `
  @keyframes v2-fx-fade-in { from { opacity: 0; } }
  .v2-fx-fade-in { animation: v2-fx-fade-in 0.7s ease-out both; }
  .v2-fx-drawn { height: 780px; }
  @media (max-width: 640px) { .v2-fx-drawn { height: 640px; } }
  @media (prefers-reduced-motion: reduce) {
    .v2-fx-motion { animation: none !important; transition: none !important; }
  }
`;

export function V2BackgroundEffect({ effect: raw, logoUrl, page, tint, accent, eventName, startDate, timezone, watermarkPlacement, outlineWord }: V2BackgroundEffectProps) {
  // Render all effects client-only to avoid hydration mismatches
  // (color-mix() CSS values normalize differently on server vs client)
  const [mounted, setMounted] = useState(false);
  const probe = useRef<HTMLSpanElement>(null);
  const [found, setFound] = useState<{ accent: string; name: string }>({ accent: '', name: '' });
  const effect = normaliseBackgroundEffect(raw);
  const drawn = effect !== 'none' && effect !== 'accent-glow' && effect !== 'logo-watermark';

  useEffect(() => setMounted(true), []);
  useAnimationStyles('v2-motion', REDUCED_MOTION_CSS);

  // What the page already knows and didn't pass: its accent and its heading.
  useEffect(() => {
    if (!mounted || !drawn) return;
    const host = probe.current?.parentElement;
    if (!host) return;
    const view = host.ownerDocument.defaultView;
    const cssAccent = view ? view.getComputedStyle(host).getPropertyValue('--theme-secondary').trim() : '';
    const h1 = host.querySelector('h1')?.textContent?.trim() ?? '';
    // A frame later, so the hero (often client-only too) has rendered its heading.
    const raf = requestAnimationFrame(() => {
      const name = host.querySelector('h1')?.textContent?.trim() || h1;
      setFound((f) => (f.accent === cssAccent && f.name === name ? f : { accent: cssAccent, name }));
    });
    return () => cancelAnimationFrame(raf);
  }, [mounted, drawn]);

  if (effect === 'none') return null;
  if (!mounted) return <span ref={probe} hidden />;

  if (drawn) {
    const name = eventName || found.name;
    const ink = accent || found.accent;
    return (
      <>
        <span ref={probe} hidden />
        {page && tint && ink && (
          <div
            className="v2-fx-drawn v2-fx-motion v2-fx-fade-in pointer-events-none"
            style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 0, overflow: 'hidden' }}
            aria-hidden="true"
          >
            <DrawnEffectLayer
              effect={effect}
              page={page}
              accent={ink}
              text={tint}
              seed={effectSeed(name)}
              word={outlineWordFor(outlineWord, name, startDate, timezone)}
              headingFont="var(--fv-heading-font, inherit)"
              headingWeight="var(--fv-heading-weight, 700)"
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="pointer-events-none fixed top-0 left-0"
      style={{ zIndex: 0, overflow: 'hidden', width: '100vw', height: '100vh' }}
      aria-hidden="true"
    >
      {effect === 'accent-glow' && <AccentGlow />}
      {effect === 'logo-watermark' && page && tint && (
        <LogoWatermarkLayer logoUrl={logoUrl} page={page} tint={tint} placement={normaliseWatermarkPlacement(watermarkPlacement)} />
      )}
    </div>
  );
}

const abs0: React.CSSProperties = { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

/** The accent glow; previews set --theme-secondary on their own box. */
export function AccentGlow() {
  return (
    <div
      style={{
        ...abs0,
        backgroundImage: [
          `radial-gradient(ellipse 70% 45% at 15% 0%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 50%, transparent) 0%, transparent 75%)`,
          `radial-gradient(ellipse 50% 30% at 80% 5%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 25%, transparent) 0%, transparent 60%)`,
          `radial-gradient(ellipse 40% 25% at 50% 50%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 10%, transparent) 0%, transparent 70%)`,
        ].join(', '),
      }}
    />
  );
}

/** Inject @keyframes into <head> once on mount */
function useAnimationStyles(id: string, css: string) {
  useEffect(() => {
    const fullId = `v2-fx-${id}`;
    if (document.getElementById(fullId)) return;
    const style = document.createElement('style');
    style.id = fullId;
    style.textContent = css;
    document.head.appendChild(style);
  }, [id, css]);
}
